import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent,
  TextField, Button, Stepper, Step, StepLabel, StepContent,
  IconButton, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel, List, ListItem, ListItemText, ListItemIcon, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Paper, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Add, Delete, ArrowBack, Code, PlayArrow, CheckCircle, Info, Schedule, Refresh,
  ExpandMore, Speed, Security, Autorenew, TrendingDown, Verified, Visibility, VisibilityOff
} from '@mui/icons-material';
import Navbar from '../../shared/Navbar';
import Breadcrumb from '../../shared/Breadcrumb';
import { COLORS } from '../../../shared/colors';
import { CONTEXT_FILES, GENERIC_STRINGS, PROJECT_CONFIG_STATUS, URL_PATTERNS, CHARACTER_LIMITS } from '../../../shared/appConstants';
import { REGEX } from '../../../shared/regex';
import { useTranslation } from 'react-i18next';
import ProjectConfigCard from '../components/ProjectConfigCard';
import { useDispatch } from 'react-redux';
import api from '../../../services/axios/api-service';
import { ENDPOINTS } from '../../../services/endpoints';
import { createAIProjectSetting, fetchAIProjectSettings, deleteAIProjectSetting, fetchAIPlanDropdown } from '../../../store';
import { toast } from 'react-toastify';
import './AIDeveloperSettings.scss';

// Pure helper functions moved to module scope to avoid recreation on each render
const isValidGitHubUrl = (url) => {
  return URL_PATTERNS.GITHUB_REPO_REGEX.test(url);
};

const isEmptyOrWhitespace = (value) => {
  return !value || typeof value !== 'string' || value.trim().length === 0;
};

const isValidBotToken = (value) => {
  if (!value || typeof value !== 'string') return false;
  // Allow alphanumeric characters, underscore, and hyphen
  return REGEX.botToken.test(value);
};

const safeTrim = (value) => {
  return value && typeof value === 'string' ? value.trim() : value;
};

const getDefaultRepository = () => ({
  name: '',
  url: '',
  baseBranch: 'main',
  branchNameFormat: 'feature/{ticket-id}-{description}',
  repositoryType: '',
  repositoryTypes: []
});

const AIDeveloperSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  const [showContextDialog, setShowContextDialog] = useState(false);
  const [generatingFiles, setGeneratingFiles] = useState(false);
  const [creatingConfig, setCreatingConfig] = useState(false);
  const [configCreated, setConfigCreated] = useState(false);
  const [configFailed, setConfigFailed] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showBotToken, setShowBotToken] = useState(false);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [repositoryTypesLoading, setRepositoryTypesLoading] = useState(false);
  const [repositoryTypes, setRepositoryTypes] = useState([]);


  const [projectConfig, setProjectConfig] = useState({
    botName: '',
    botToken: '',
    repositories: [{
      name: '',
      url: '',
      baseBranch: 'main',
      branchNameFormat: 'feature/{ticket-id}-{description}',
      repositoryType: '',
      repositoryTypes: []
    }]
  });

  useEffect(() => {
    fetchData();
    loadSavedConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSavedConfigs = useCallback(async () => {
    try {
      setRefreshing(true);
      const result = await dispatch(fetchAIProjectSettings()).unwrap();
      const configs = (result.data || []).map(config => {
        const pullRequests = config.pullRequests || [];
        const gitRepos = config.git_repos || [];
        return {
          id: config.id,
          spaceId: config.space_id,
          projectId: config.project_id,
          projectName: config.project_name || 'Unknown Project',
          status: config.status.toLowerCase(),
          botName: config.ai_bot_name,
          botToken: config.bot_token,
          ticket: config.ticket || null,
          repositories: gitRepos.map(repo => {
            const repoPR = pullRequests.find(pr => {
              const nameMatch = pr.git_repo_name && repo.git_repo_name && pr.git_repo_name === repo.git_repo_name;
              const urlMatch = pr.pr_url && repo.repo_url && pr.pr_url.includes(repo.repo_url.replace(URL_PATTERNS.GITHUB_BASE, '').replace(URL_PATTERNS.GIT_EXTENSION, ''));
              return nameMatch ? (nameMatch && urlMatch) : urlMatch;
            });
            return {
              name: repo.git_repo_name,
              url: repo.url || repo.repo_url,
              baseBranch: repo.base_branch || 'main',
              branchNameFormat: repo.branch_name_format,
              repositoryType: repo.repository_type || '',
              repositoryTypes: [],
              prStatus: repoPR?.state || null,
              prUrl: repoPR?.pr_url || null
            };
          }),
          created_at: config.created_at
        };
      });
      setSavedConfigs(configs);
    } catch (error) {
      setSavedConfigs([]);
      toast.error(t('aiDeveloperSettings.messages.loadConfigsFailed'));
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, t]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setSpacesLoading(true);
      const dropdownsRes = await api.get(ENDPOINTS.PROJECTS_DROPDOWNS);
      const dropdownData = dropdownsRes.data?.data || dropdownsRes.data || {};
      
      const spacesData = dropdownData.spaces || [];
      setSpaces(spacesData);
    } catch (error) {
      setSpaces([]);
      toast.error(`Failed to fetch data: ${error.message}`);
    } finally {
      setLoading(false);
      setSpacesLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async (spaceId) => {
    try {
      const projectsRes = await api.get(`${ENDPOINTS.PROJECT_DROPDOWNS_BY_USER}?space_id=${spaceId}`);
      const projectsData = projectsRes.data?.data || [];
      setFilteredProjects(projectsData);
    } catch (error) {
      setFilteredProjects([]);
      toast.error(`Failed to fetch projects: ${error.message}`);
    }
  }, []);

  const handleSpaceChange = useCallback(async (spaceId) => {
    setSelectedSpace(spaceId);
    setSelectedProject('');
    // Reset project config when space changes
    setProjectConfig({
      botName: '',
      botToken: '',
      repositories: [getDefaultRepository()]
    });
    if (spaceId) {
      setProjectsLoading(true);
      setActiveStep(1);
      await fetchProjects(spaceId);
      setProjectsLoading(false);
    } else {
      setFilteredProjects([]);
      setActiveStep(0);
    }
  }, [fetchProjects]);

  const handleProjectChange = useCallback(async (projectId) => {
    setSelectedProject(projectId);
    // Reset project config when project changes
    setProjectConfig({
      botName: '',
      botToken: '',
      repositories: [getDefaultRepository()]
    });
    if (projectId) {
      try {
        setRepositoryTypesLoading(true);
        setActiveStep(2);
        const repositoryTypesRes = await dispatch(fetchAIPlanDropdown({ projectId, type: 'repository_type' })).unwrap();
        const types = repositoryTypesRes.data?.repository_type || [];
        setRepositoryTypes(types);
        
        setProjectConfig(prev => ({
          ...prev,
          repositories: prev.repositories.map(repo => ({
            ...repo,
            repositoryTypes: types
          }))
        }));
      } catch (error) {
        setRepositoryTypes([]);
        setProjectConfig(prev => ({
          ...prev,
          repositories: prev.repositories.map(repo => ({
            ...repo,
            repositoryTypes: []
          }))
        }));
      } finally {
        setRepositoryTypesLoading(false);
      }
    }
  }, [dispatch]);



  // Update project configuration fields
  const updateProjectConfig = (field, value) => {
    setProjectConfig(prev => ({ ...prev, [field]: value }));
  };

  // Handle repository array operations (add/remove/update)
  const updateRepositoryArray = (action, index = null, field = null, value = null) => {
    setProjectConfig(prev => {
      switch (action) {
        case GENERIC_STRINGS.ADD:
          return {
            ...prev,
            repositories: [...prev.repositories, {
              name: '',
              url: '',
              baseBranch: 'main',
              branchNameFormat: 'feature/{ticket-id}-{description}',
              repositoryType: '',
              repositoryTypes: repositoryTypes
            }]
          };
        case GENERIC_STRINGS.REMOVE:
          return {
            ...prev,
            repositories: prev.repositories.filter((_, idx) => idx !== index)
          };
        case GENERIC_STRINGS.UPDATE:
          return {
            ...prev,
            repositories: prev.repositories.map((repo, idx) =>
              idx === index ? { ...repo, [field]: value } : repo
            )
          };
        default:
          return prev;
      }
    });
  };

  // Configuration handlers using common update functions
  const handleProjectFieldChange = (field, value) => updateProjectConfig(field, value);
  const handleRepositoryChange = (repoIndex, field, value) => updateRepositoryArray(GENERIC_STRINGS.UPDATE, repoIndex, field, value);
  const addRepository = () => updateRepositoryArray(GENERIC_STRINGS.ADD);
  const removeRepository = (repoIndex) => updateRepositoryArray(GENERIC_STRINGS.REMOVE, repoIndex);

  const handleNext = () => {
    if (activeStep === 2) {
      setActiveStep(3);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
  };

  const handleGenerateContext = async () => {
    // Validate bot name - cannot be empty or just whitespace
    if (isEmptyOrWhitespace(projectConfig.botName)) {
      toast.error(t('aiDeveloperSettings.messages.fillBotNameToken'));
      return;
    }
    
    // Validate bot token - cannot be empty or just whitespace
    if (isEmptyOrWhitespace(projectConfig.botToken)) {
      toast.error(t('aiDeveloperSettings.messages.fillBotNameToken'));
      return;
    }
    
    // Validate bot token format - must contain only alphanumeric, underscore, and hyphen
    if (!isValidBotToken(projectConfig.botToken)) {
      toast.error(t('aiDeveloperSettings.messages.botTokenInvalidFormat'));
      return;
    }
    
    // Validate repositories exist
    if (projectConfig.repositories.length === 0) {
      toast.error(t('aiDeveloperSettings.messages.addRepository'));
      return;
    }
    
    // Validate all repository fields - cannot be empty or just whitespace
    const hasEmptyRepoField = projectConfig.repositories.some(repo => 
      isEmptyOrWhitespace(repo.name) ||
      isEmptyOrWhitespace(repo.url) ||
      isEmptyOrWhitespace(repo.baseBranch) ||
      isEmptyOrWhitespace(repo.branchNameFormat) ||
      isEmptyOrWhitespace(repo.repositoryType)
    );
    
    if (hasEmptyRepoField) {
      toast.error(t('aiDeveloperSettings.messages.fillRepositoryFields'));
      return;
    }
    
    // Validate character length limits
    const tooLongFields = [];
    
    // Check bot name
    if (projectConfig.botName && projectConfig.botName.length > CHARACTER_LIMITS.BOT_NAME) {
      tooLongFields.push(t('aiDeveloperSettings.fields.botName'));
    }
    
    // Check repository fields
    projectConfig.repositories.forEach((repo, index) => {
      if (repo.name && repo.name.length > CHARACTER_LIMITS.REPO_NAME) {
        tooLongFields.push(`${t('aiDeveloperSettings.fields.repositoryName')} ${index + 1}`);
      }
      if (repo.url && repo.url.length > CHARACTER_LIMITS.REPO_URL) {
        tooLongFields.push(`${t('aiDeveloperSettings.fields.repositoryUrl')} ${index + 1}`);
      }
      if (repo.baseBranch && repo.baseBranch.length > CHARACTER_LIMITS.BASE_BRANCH_NAME) {
        tooLongFields.push(`${t('aiDeveloperSettings.fields.baseBranch')} ${index + 1}`);
      }
    });
    
    if (tooLongFields.length > 0) {
      toast.error(t('aiDeveloperSettings.messages.textTooLong', { fields: tooLongFields.join(', ') }));
      return;
    }
    
    // Validate GitHub URLs format
    if (projectConfig.repositories.some(repo => !isValidGitHubUrl(repo.url))) {
      toast.error(t('aiDeveloperSettings.messages.invalidGitHubUrls'));
      return;
    }
    
    setShowContextDialog(true);
  };

  // Transform config data to API payload format
  const createConfigPayload = useCallback((config) => ({
    space_id: selectedSpace,
    project_id: selectedProject,
    status: config.status ? config.status.toUpperCase() : PROJECT_CONFIG_STATUS.ACTIVE.toUpperCase(),
    ai_bot_name: safeTrim(config.botName),
    bot_token: safeTrim(config.botToken),
    git_repos: config.repositories.map(repo => ({
      git_repo_name: safeTrim(repo.name),
      repo_url: safeTrim(repo.url),
      base_branch: safeTrim(repo.baseBranch),
      branch_name_format: safeTrim(repo.branchNameFormat),
      repository_type: safeTrim(repo.repositoryType)
    }))
  }), [selectedSpace, selectedProject]);

  const handleConfirmGenerate = async () => {
    try {
      setShowContextDialog(false);
      setConfigFailed(false);
      setCreatingConfig(true);
      setActiveStep(4);
      
      const payload = createConfigPayload(projectConfig);
      await dispatch(createAIProjectSetting(payload)).unwrap();
      setCreatingConfig(false);
      setGeneratingFiles(true);
      setConfigCreated(true);
      toast.success(t('aiDeveloperSettings.messages.configCreated'));
    } catch (error) {
      setCreatingConfig(false);
      setGeneratingFiles(false);
      setConfigFailed(true);
      setConfigCreated(false);
      toast.error(error.message || t('aiDeveloperSettings.messages.configCreateFailed'));
    }
  };

  const resetForm = () => {
    setSelectedSpace('');
    setSelectedProject('');
    setProjectConfig({
      botName: '',
      botToken: '',
      repositories: [getDefaultRepository()]
    });
    setActiveStep(0);
    setEditingConfig(null);
    setEditingConfigId(null);
    setGeneratingFiles(false);
    setConfigCreated(false);
    setConfigFailed(false);
  };

  const handleEditConfig = useCallback((config) => {
    setEditingConfigId(config.id);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingConfigId(null);
  }, []);

  const handleSaveEdit = useCallback(async (configId, updatedConfig) => {
    try {
      const payload = createConfigPayload(updatedConfig);
      await api.put(ENDPOINTS.AI_PROJECT_SETTING_BY_ID(configId), payload);
      toast.success(t('aiDeveloperSettings.messages.configUpdated'));
      setEditingConfigId(null);
      loadSavedConfigs();
    } catch (error) {
      toast.error(t('aiDeveloperSettings.messages.configUpdateFailed'));
    }
  }, [createConfigPayload, t, loadSavedConfigs]);

  const handleDeleteConfig = useCallback(async (configId) => {
    try {
      await dispatch(deleteAIProjectSetting(configId)).unwrap();
      toast.success(t('aiDeveloperSettings.messages.configDeleted'));
      loadSavedConfigs();
    } catch (error) {
      toast.error(t('aiDeveloperSettings.messages.configDeleteFailed'));
    }
  }, [dispatch, t, loadSavedConfigs]);

  const handleNewConfig = () => {
    if (generatingFiles) {
      toast.warning(t('aiDeveloperSettings.messages.waitForCompletion'));
      return;
    }
    resetForm();
    setActiveTab(1);
  };

  const handleSaveConfig = () => {
    resetForm();
    setActiveTab(0);
    loadSavedConfigs();
  };



  // Render step content based on current step and open state
  // Each step shows either a form (when open) or a status badge (when closed but completed)
  const getStepContent = (step, isOpen) => {
    switch (step) {
      case 0: // Space Selection Step
        // Show selected space badge when step is closed but space is selected
        if (!isOpen && selectedSpace) {
          return (
            <Typography variant="body2" color="primary" className="step-status-badge">
              {t('aiDeveloperSettings.status.selected')} {spaces.find(s => (s._id || s.id) === selectedSpace)?.name || selectedSpace}
            </Typography>
          );
        }
        // Show space selection form when step is open
        return isOpen ? (
          <Card elevation={2}>
            <CardContent>
              {spacesLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="60px">
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl fullWidth size="medium">
                  <InputLabel>Choose Space</InputLabel>
                  <Select
                    value={selectedSpace}
                    onChange={(e) => handleSpaceChange(e.target.value)}
                    label="Choose Space"
                    MenuProps={{
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: { 
                        style: { 
                          maxHeight: 300
                        } 
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('aiDeveloperSettings.placeholders.selectSpace')}</em>
                    </MenuItem>
                    {!Array.isArray(spaces) || spaces.length === 0 ? (
                      <MenuItem disabled>
                        <em>{t('aiDeveloperSettings.noOptions.spaces')}</em>
                      </MenuItem>
                    ) : (
                      spaces.map((space) => (
                        <MenuItem key={space._id || space.id} value={space._id || space.id}>
                          {space.name || space.title || `Space ${space._id || space.id}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
        ) : null;

      case 1: // Project Selection Step
        // Show selected project badge when step is closed but project is selected
        if (!isOpen && selectedProject) {
          return (
            <Typography variant="body2" color="primary" className="step-status-badge">
              {t('aiDeveloperSettings.status.selected')} {filteredProjects.find(p => (p._id || p.id) === selectedProject)?.name || selectedProject}
            </Typography>
          );
        }
        // Show project selection form when step is open
        return isOpen ? (
          <Card elevation={2}>
            <CardContent>
              {projectsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="60px">
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl fullWidth size="medium">
                  <InputLabel>Choose Project</InputLabel>
                  <Select
                    value={selectedProject}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    label="Choose Project"
                    MenuProps={{
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      autoWidth: false,
                      PaperProps: { 
                        style: { 
                          maxHeight: 300
                        } 
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('aiDeveloperSettings.placeholders.selectProject')}</em>
                    </MenuItem>
                    {!Array.isArray(filteredProjects) || filteredProjects.length === 0 ? (
                      <MenuItem disabled>
                        <em>{t('aiDeveloperSettings.noOptions.projects')}</em>
                      </MenuItem>
                    ) : (
                      filteredProjects.map((project) => (
                        <MenuItem key={project._id || project.id} value={project._id || project.id}>
                          {project.name || project.title || `Project ${project._id || project.id}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
        ) : null;

      case 2: // Bot & Repository Configuration Step
        // Show loading indicator when fetching repository types
        if (!isOpen && repositoryTypesLoading) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" height="40px">
              <CircularProgress size={20} />
            </Box>
          );
        }
        // Show configuration summary when step is closed but configured
        if (!isOpen) {
          return (
            <Typography variant="body2" color="primary" className="step-status-badge">
              {t('aiDeveloperSettings.status.configured')} {projectConfig.repositories.length} {projectConfig.repositories.length > 1 ? t('aiDeveloperSettings.status.repositories') : t('aiDeveloperSettings.status.repository')}
            </Typography>
          );
        }
        // Show bot and repository configuration form when step is open
        return isOpen ? (
          <Card elevation={2}>
            <CardContent>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('aiDeveloperSettings.fields.botName')}
                        value={projectConfig.botName}
                        onChange={(e) => handleProjectFieldChange('botName', e.target.value)}
                        placeholder={t('aiDeveloperSettings.placeholders.botName')}
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('aiDeveloperSettings.fields.githubToken')}
                        type={showBotToken ? 'text' : 'password'}
                        value={projectConfig.botToken}
                        onChange={(e) => handleProjectFieldChange('botToken', e.target.value)}
                        placeholder={t('aiDeveloperSettings.placeholders.githubToken')}
                        autoComplete="new-password"
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              size="small"
                              onClick={() => setShowBotToken(!showBotToken)}
                              edge="end"
                            >
                              {showBotToken ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    {t('aiDeveloperSettings.fields.repositories')}
                  </Typography>
                  {projectConfig.repositories.map((repo, index) => (
                    <Paper key={index} sx={{ p: 3, mb: 2, bgcolor: COLORS.gray[50] }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          {t('aiDeveloperSettings.fields.repositoryName')} {index + 1}
                        </Typography>
                        <IconButton
                          onClick={() => removeRepository(index)}
                          disabled={projectConfig.repositories.length === 1}
                          size="small"
                          color="error"
                          sx={{ p: 0.5 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('aiDeveloperSettings.fields.repositoryName')}
                            value={repo.name}
                            onChange={(e) => handleRepositoryChange(index, 'name', e.target.value)}
                            placeholder={t('aiDeveloperSettings.placeholders.repositoryName')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('aiDeveloperSettings.fields.repositoryUrl')}
                            value={repo.url}
                            onChange={(e) => handleRepositoryChange(index, 'url', e.target.value)}
                            placeholder={t('aiDeveloperSettings.placeholders.repositoryUrl')}
                            error={repo.url && !isValidGitHubUrl(repo.url)}
                            helperText={repo.url && !isValidGitHubUrl(repo.url) ? "Please enter a valid GitHub URL" : ""}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('aiDeveloperSettings.fields.baseBranch')}
                            value={repo.baseBranch}
                            onChange={(e) => handleRepositoryChange(index, 'baseBranch', e.target.value)}
                            placeholder={t('aiDeveloperSettings.placeholders.baseBranch')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <TextField
                              fullWidth
                              label={t('aiDeveloperSettings.fields.branchNameFormat')}
                              value={repo.branchNameFormat || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= CHARACTER_LIMITS.BRANCH_FORMAT) {
                                  handleRepositoryChange(index, 'branchNameFormat', value);
                                }
                              }}
                              placeholder={t('aiDeveloperSettings.placeholders.branchNameFormat')}
                              inputProps={{ maxLength: CHARACTER_LIMITS.BRANCH_FORMAT }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                              {(repo.branchNameFormat || '').length}/{CHARACTER_LIMITS.BRANCH_FORMAT}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            select
                            label={t('aiDeveloperSettings.fields.repositoryType')}
                            value={repo.repositoryType || ''}
                            onChange={(e) => handleRepositoryChange(index, 'repositoryType', e.target.value)}
                            sx={{ minWidth: '200px', width: '100%' }}
                            SelectProps={{
                              MenuProps: {
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                autoWidth: false,
                                PaperProps: { 
                                  style: { 
                                    maxHeight: 300,
                                    minWidth: '200px'
                                  } 
                                }
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>{t('aiDeveloperSettings.placeholders.selectRepositoryType')}</em>
                            </MenuItem>
                            {!repo.repositoryTypes || repo.repositoryTypes.length === 0 ? (
                              <MenuItem disabled>
                                <em>{t('aiDeveloperSettings.noOptions.repositoryTypes')}</em>
                              </MenuItem>
                            ) : (
                              repo.repositoryTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))
                            )}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Typography
                    onClick={addRepository}
                    className="add-repository-link"
                  >
                    <Add sx={{ fontSize: 16 }} />
                    {t('aiDeveloperSettings.actions.addRepository')}
                  </Typography>

                  <Box 
                    onClick={handleNext}
                    className="continue-container"
                  >
                    <Typography 
                      className="continue-text"
                      variant="body1"
                    >
                      {t('aiDeveloperSettings.actions.continueToContext')}
                      <ArrowBack 
                        className="continue-arrow"
                      />
                    </Typography>
                  </Box>
            </CardContent>
          </Card>
        ) : null;

      case 3: // Context Generation Step
        // Show generation status when step is closed but files are being generated
        if (!isOpen && (generatingFiles || creatingConfig)) {
          return (
            <Typography variant="body2" color="primary" className="step-status-badge">
              {t('aiDeveloperSettings.contextGeneration.filesBeingGenerated.title')}
            </Typography>
          );
        }
        // Show context generation benefits and file list when step is open
        return isOpen ? (
          <Card elevation={2}>
            <CardContent>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('aiDeveloperSettings.contextGeneration.description')}
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                 {t('aiDeveloperSettings.contextGeneration.title')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'start' }}>
                    <Speed sx={{ color: COLORS.primary, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" className="benefit-title">{t('aiDeveloperSettings.contextGeneration.benefits.betterCodeQuality.title')}</Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        {t('aiDeveloperSettings.contextGeneration.benefits.betterCodeQuality.description')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'start' }}>
                    <Security sx={{ color: COLORS.primary, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" className="benefit-title">{t('aiDeveloperSettings.contextGeneration.benefits.fewerMistakes.title')}</Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        {t('aiDeveloperSettings.contextGeneration.benefits.fewerMistakes.description')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'start' }}>
                    <Autorenew sx={{ color: COLORS.primary, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" className="benefit-title">{t('aiDeveloperSettings.contextGeneration.benefits.lessRepetition.title')}</Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        {t('aiDeveloperSettings.contextGeneration.benefits.lessRepetition.description')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'start' }}>
                    <TrendingDown sx={{ color: COLORS.primary, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" className="benefit-title">{t('aiDeveloperSettings.contextGeneration.benefits.lowerTokenUsage.title')}</Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        {t('aiDeveloperSettings.contextGeneration.benefits.lowerTokenUsage.description')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'start' }}>
                    <Verified sx={{ color: COLORS.primary, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" className="benefit-title">{t('aiDeveloperSettings.contextGeneration.benefits.moreConsistency.title')}</Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        {t('aiDeveloperSettings.contextGeneration.benefits.moreConsistency.description')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'start' }}>
                    <Security sx={{ color: COLORS.primary, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" className="benefit-title">{t('aiDeveloperSettings.contextGeneration.benefits.saferChanges.title')}</Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        {t('aiDeveloperSettings.contextGeneration.benefits.saferChanges.description')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Accordion sx={{ mb: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight="600">
                    {t('aiDeveloperSettings.contextGeneration.contextFilesTitle')} ({CONTEXT_FILES.length} files)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, bgcolor: COLORS.gray[50] }}>
                  <List sx={{ p: 0 }}>
                    {Array.isArray(CONTEXT_FILES) && CONTEXT_FILES.map((file, index) => (
                      <ListItem key={`context-file-detail-${index}-${file.name}`} sx={{ py: 1.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Code color="primary" sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={<Typography variant="subtitle2" fontWeight="600">{file.name}</Typography>}
                          secondary={file.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              <Box display="flex" gap={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={handleBack}
                >
                  {t('aiDeveloperSettings.actions.back')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={handleGenerateContext}
                  sx={{ bgcolor: COLORS.primary }}
                >
                  {t('aiDeveloperSettings.actions.generateContextFiles')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : null;

      case 4: // File Generation Status Step
        // Always show content when step is open (no closed state for this step)
        return isOpen ? (
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
              {/* Show different content based on generation state */}
              {creatingConfig ? ( // Configuration creation in progress
                <>
                  <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                    <CircularProgress size={40} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Creating Configuration...
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please wait while we set up your AI Developer configuration.
                  </Typography>
                </>
              ) : configFailed ? (
                <>
                  <Typography variant="h5" sx={{ mb: 2, color: COLORS.aiDeveloperSettings.errorText }}>
                    Oops! Something went wrong
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    There was an error creating your configuration. Please try again.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setConfigFailed(false);
                      setActiveStep(3);
                    }}
                    sx={{ bgcolor: COLORS.primary }}
                  >
                    Try Again
                  </Button>
                </>
              ) : configCreated ? (
                <>
                  <Box className="generating-icon-container">
                    <Schedule sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography 
                    variant="h5" 
                    className="generating-title"
                  >
                    Files Being Generated
                  </Typography>

                  <Typography 
                    variant="body2" 
                    className="generating-description"
                  >
                    {t('aiDeveloperSettings.contextGeneration.filesBeingGenerated.description')}
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-icon': { mr: 1 } }}>
                    {t('aiDeveloperSettings.contextGeneration.filesBeingGenerated.alert')}
                  </Alert>
                  <Box display="flex" justifyContent="center" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setActiveTab(0);
                        loadSavedConfigs();
                      }}
                    >
                      {t('aiDeveloperSettings.actions.goToConfigurations')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveConfig}
                      sx={{ bgcolor: COLORS.primary }}
                    >
                      {t('aiDeveloperSettings.actions.done')}
                    </Button>
                  </Box>
                </>
              ) : null}
            </CardContent>
          </Card>
        ) : null;



      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Navbar />
        <main className="main">
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Navbar />
      
      <main className="main">
        <Breadcrumb 
          items={[
            { label: 'Settings', path: '/settings' },
            { label: 'AI Developer Settings', path: null }
          ]}
        />
        
        <div className="ai-developer-settings">
          <Box sx={{ mb: 3, pt: 0 }}>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              {t('aiDeveloperSettings.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {t('aiDeveloperSettings.description')}
            </Typography>
          </Box>

          <Box className="tabs-container">
            {/* Main navigation tabs */}
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                if (newValue === 0) {
                  loadSavedConfigs();
                }
              }} 
              className="config-tabs"
            >
              <Tab label={t('aiDeveloperSettings.tabs.configurations')} />
              <Tab label={editingConfig ? t('aiDeveloperSettings.tabs.editConfiguration') : t('aiDeveloperSettings.tabs.newConfiguration')} />
            </Tabs>
            {activeTab === 0 && (
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={loadSavedConfigs}
                disabled={refreshing}
                className="refresh-button"
              >
                {t('aiDeveloperSettings.refresh')}
              </Button>
            )}
            {activeTab === 1 && (
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={resetForm}
                className="refresh-button"
              >
                Reset
              </Button>
            )}
          </Box>

          <div className="tab-content">
          {/* Configurations list tab */}
          {activeTab === 0 && (
            <div className="configs-tab">
              {refreshing ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                  <CircularProgress />
                </Box>
              ) : savedConfigs.length === 0 ? (
                <Card className="empty-state-card" elevation={2}>
                  <CardContent>
                    <Code className="empty-state-icon" />
                    <Typography variant="h6" className="empty-state-title">
                      {t('aiDeveloperSettings.emptyState.title')}
                    </Typography>
                    <Typography variant="body2" className="empty-state-description">
                      {t('aiDeveloperSettings.emptyState.description')}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleNewConfig}
                      className="empty-state-button"
                    >
                      {t('aiDeveloperSettings.actions.createConfiguration')}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="configs-grid">
                  {savedConfigs.map((config) => (
                    <ProjectConfigCard 
                      key={config.id} 
                      config={config}
                      isEditing={editingConfigId === config.id}
                      onEdit={handleEditConfig}
                      onDelete={handleDeleteConfig}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Configuration creation/editing tab */}
          {activeTab === 1 && (
            <div className="config-form-tab">
              <div className="stepper-container">
                {/* Multi-step configuration wizard with conditional navigation */}
                <Stepper activeStep={generatingFiles ? 4 : activeStep} orientation="vertical">
          {/* Step 1: Space Selection - Always expanded, clickable only if config not created */}
          <Step expanded>
            <StepLabel 
              onClick={() => !configCreated && setActiveStep(activeStep === 0 ? -1 : 0)}
              sx={{ cursor: configCreated ? 'default' : 'pointer' }}
            >
              {t('aiDeveloperSettings.stepper.selectSpace')}
            </StepLabel>
            <StepContent>
              {/* Renders space selection dropdown or selected space badge */}
              {getStepContent(0, activeStep === 0)}
            </StepContent>
          </Step>
          {/* Step 2: Project Selection - Expanded only if space selected */}
          <Step expanded={selectedSpace}>
            <StepLabel 
              onClick={() => selectedSpace && !configCreated && setActiveStep(activeStep === 1 ? -1 : 1)}
              sx={{ cursor: selectedSpace && !configCreated ? 'pointer' : 'default' }}
            >
              {t('aiDeveloperSettings.stepper.selectProject')}
            </StepLabel>
            <StepContent>
              {/* Renders project selection dropdown or selected project badge */}
              {getStepContent(1, activeStep === 1)}
            </StepContent>
          </Step>
          {/* Step 3: Bot & Repository Configuration - Expanded only if project selected */}
          <Step expanded={selectedProject}>
            <StepLabel 
              onClick={() => selectedProject && !configCreated && setActiveStep(activeStep === 2 ? -1 : 2)}
              sx={{ cursor: selectedProject && !configCreated ? 'pointer' : 'default' }}
            >
              {t('aiDeveloperSettings.stepper.configureBotRepositories')}
            </StepLabel>
            <StepContent>
              {/* Renders bot config form and repository management */}
              {getStepContent(2, activeStep === 2)}
            </StepContent>
          </Step>
          {/* Step 4: Context Generation - Expanded only if project selected */}
          <Step expanded={selectedProject}>
            <StepLabel 
              onClick={() => selectedProject && !configCreated && setActiveStep(activeStep === 3 ? -1 : 3)}
              sx={{ cursor: selectedProject && !configCreated ? 'pointer' : 'default' }}
            >
              {t('aiDeveloperSettings.stepper.generateContextFiles')}
            </StepLabel>
            <StepContent>
              {/* Renders context generation benefits and file list */}
              {getStepContent(3, activeStep === 3)}
            </StepContent>
          </Step>
          {/* Step 5: File Generation Status - Expanded only during generation */}
          <Step expanded={generatingFiles}>
            <StepLabel 
              onClick={() => generatingFiles && setActiveStep(activeStep === 4 ? -1 : 4)}
              sx={{ cursor: generatingFiles ? 'pointer' : 'default' }}
            >
              {t('aiDeveloperSettings.stepper.generatingFiles')}
            </StepLabel>
            <StepContent>
              {/* Renders generation progress, success, or error states */}
              {getStepContent(4, activeStep === 4)}
            </StepContent>
          </Step>
                </Stepper>
              </div>

        {/* Context files generation confirmation dialog */}
        <Dialog
          open={showContextDialog}
          onClose={() => setShowContextDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { p: 3 }
          }}
        >
          <DialogTitle sx={{ pt: 0, pr: 0, pl: 0, pb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info color="primary" />
              {t('aiDeveloperSettings.contextGeneration.dialogTitle')}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('aiDeveloperSettings.contextGeneration.dialogDescription')}
            </Typography>
            <List>
              {Array.isArray(CONTEXT_FILES) && CONTEXT_FILES.map((file, index) => (
                <ListItem key={`context-file-${index}-${file.name}`}>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={file.name}
                  />
                </ListItem>
              ))}
            </List>
            <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
              {t('aiDeveloperSettings.contextGeneration.dialogAlert')}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowContextDialog(false)}>{t('aiDeveloperSettings.actions.cancel')}</Button>
            <Button 
              variant="contained" 
              onClick={handleConfirmGenerate}
              sx={{ bgcolor: COLORS.primary }}
            >
              {t('aiDeveloperSettings.actions.confirmCreate')}
            </Button>
          </DialogActions>
        </Dialog>
            </div>
          )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIDeveloperSettings;